import { Modal } from '@/components/Modal';

import { useDeleteClaim } from '@/hooks/useDeleteClaim';

interface DeleteClaimModalProps {
  claimId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

function DeleteClaimModal({ claimId, onCancel, onSuccess }: DeleteClaimModalProps) {
  const { cancel, isPending, error, reset } = useDeleteClaim();

  const handleClose = () => {
    if (isPending) return;
    reset();
    onCancel();
  };

  const handleConfirm = async () => {
    reset();
    try {
      await cancel(claimId);
      onSuccess();
    } catch {
      // Error is captured and managed by the useDeleteClaim hook.
    }
  };

  return (
    <Modal
      title="Delete Claim"
      onClose={handleClose}
      maxWidth="sm"
      zIndex={70}
      closeButtonDisabled={isPending}
      closeOnBackdropClick={!isPending}
      closeOnEscape={!isPending}
      titleSize="lg"
    >
      <div className="space-y-6" data-testid="delete-claim-modal">
        <p className="text-base text-gray-800">Are you sure you want to delete this claim?</p>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isPending}
            className="flex-1 rounded-lg bg-gray-100 px-4 py-3 font-bold text-gray-900 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            No
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isPending}
            className="flex-1 rounded-lg bg-red-600 px-4 py-3 font-bold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPending ? 'Deleting...' : 'Yes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export { DeleteClaimModal };
